import React, { useEffect, useState, useRef } from "react";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";

export default function AddEventFormFields({ onSuccess, setAddEvent }) {
  const [address, setAddress] = useState("");
  const [name, setName] = useState("");
  const [place, setPlace] = useState(null);
  const [themes, setThemes] = useState([]);
  const [selectedThemes, setSelectedThemes] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [duration, setDuration] = useState("4");
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const venueContainerRef = useRef(null);

  // ✅ Deduplication helper
  const showToast = (message, type = "error", id = message) => {
    toast[type](message, { toastId: id });
  };

  // Initialize date & time in IST on mount
  useEffect(() => {
    const now = new Date();
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
    const istMs = utcMs + 5.5 * 60 * 60 * 1000;
    const istNow = new Date(istMs + 10 * 60 * 1000);

    const yyyy = istNow.getFullYear();
    const mm = String(istNow.getMonth() + 1).padStart(2, "0");
    const dd = String(istNow.getDate()).padStart(2, "0");
    const hh = String(istNow.getHours()).padStart(2, "0");
    const min = String(istNow.getMinutes()).padStart(2, "0");

    setStartDate(`${yyyy}-${mm}-${dd}`);
    setStartTime(`${hh}:${min}`);
  }, []);

  useEffect(() => {
    fetchWithAuth(API_ENDPOINTS.VENUE_THEMES)
      .then((r) => r.json())
      .then((data) => setThemes(data))
      .catch((err) => console.error("Error fetching themes:", err));
  }, []);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSearch && !e.target.closest(".search-container")) {
        setShowSearch(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSearch]);

  const handleSelect = async (description, placeId) => {
    try {
      let venueName = description;
      let venueAddress = description;
      let lat, lng;
      if (placeId && window.google?.maps) {
        const service = new window.google.maps.places.PlacesService(
          document.createElement("div"),
        );
        const details = await new Promise((resolve, reject) => {
          service.getDetails(
            { placeId, fields: ["name", "formatted_address", "geometry"] },
            (placeResult, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                resolve(placeResult);
              } else {
                reject(status);
              }
            },
          );
        });
        venueName = details.name || venueName;
        venueAddress = details.formatted_address || venueAddress;
        lat = details.geometry.location.lat();
        lng = details.geometry.location.lng();
      } else {
        const results = await geocodeByAddress(description);
        const coords = await getLatLng(results[0]);
        [venueName] = description.split(", ");
        venueAddress = description;
        lat = coords.lat;
        lng = coords.lng;
      }
      setName(venueName);
      setAddress(venueAddress);
      setPlace({ name: venueName, address: venueAddress, lat, lng });
      setShowSearch(false);
    } catch (error) {
      console.error("Error selecting address:", error);
      showToast("Failed to fetch location details");
    }
  };

  const toggleTheme = (theme) => {
    const updated = selectedThemes.includes(theme)
      ? selectedThemes.filter((x) => x !== theme)
      : [...selectedThemes, theme];
    if (updated.length > 3) return showToast("Max 3 themes allowed", "warn");
    setSelectedThemes(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!place) return showToast("Please select a venue");
    const dur = parseFloat(duration);
    if (isNaN(dur) || dur < 1 || dur > 24) {
      return showToast("Duration must be between 1 and 24 hours");
    }
    if (selectedThemes.length === 0)
      return showToast("Min 1 themes is required");
    if (selectedThemes.length > 3) return showToast("Max 3 themes allowed");

    try {
      const [Y, M, D] = startDate.split("-").map(Number);
      const [H, m] = startTime.split(":").map(Number);
      const istMs = Date.UTC(Y, M - 1, D, H, m);
      const utcMs = istMs - 5.5 * 60 * 60 * 1000;
      const startAt = new Date(utcMs).toISOString();
      const endAt = new Date(utcMs + dur * 60 * 60 * 1000).toISOString();

      const payload = {
        name,
        geometry: { type: "Point", coordinates: [place.lng, place.lat] },
        address,
        startAt,
        endAt,
        themes: selectedThemes,
        isTringDevice: false,
      };
      const res = await fetchWithAuth(API_ENDPOINTS.VENUE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      showToast("Event created successfully!", "success", "event-success");
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Submission error:", err);
      showToast("Failed to create event", "error", "event-failed");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setAddEvent(false);
        }
      }}
    >
      <div
        className="bg-white backdrop-blur-lg rounded-xl shadow-2xl overflow-hidden w-full max-w-2xl my-2 sm:my-4 min-h-0 max-h-[calc(100vh-16px)] sm:max-h-[calc(100vh-32px)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-2 sm:p-6 bg-white">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl sm:text-3xl text-center w-full font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#651FA8] to-[#8A325D]">
                New Event
              </h1>
              <button
                onClick={() => setAddEvent(false)}
                className="absolute right-6 top-2 text-2xl text-[#651FA8] hover:text-[#8A325D] mb-2"
              >
                ×
              </button>
            </div>
          </div>
          {/* Search Modal */}
          {showSearch && (
            <div className="fixed top-0 left-0 h-full flex items-start inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-start justify-center">
              <div
                className="search-container bg-white rounded-md shadow-xl w-full max-w-2xl transform"
                onClick={(e) => e.stopPropagation()}
              >
                <PlacesAutocomplete
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onSelect={handleSelect}
                  searchOptions={{ types: ["establishment", "geocode"] }}
                >
                  {({
                    getInputProps,
                    suggestions,
                    getSuggestionItemProps,
                    loading,
                  }) => (
                    <div className="p-4">
                      <div className=" p-3 gap-3 rounded-lg flex border-2 border-[#651FA8] focus:ring-4 focus:ring-[#651FA8]/30 outline-none">
                        <input
                          {...getInputProps({
                            placeholder: "Search venues or addresses...",
                            className: "w-full text-base  outline-none",
                            autoFocus: true,
                          })}
                        />
                        <button
                          onClick={() => setShowSearch(false)}
                          className=" text-2xl text-[#651FA8] hover:text-[#8A325D]"
                        >
                          ×
                        </button>
                      </div>
                      <div className="mt-4 max-h-[60vh] overflow-y-auto">
                        {loading && (
                          <div className="p-4 text-center text-gray-500">
                            <div className="animate-pulse">
                              Searching locations...
                            </div>
                          </div>
                        )}
                        {suggestions.map((suggestion) => (
                          <div
                            {...getSuggestionItemProps(suggestion, {
                              className: `p-4 cursor-pointer hover:bg-[#651FA8]/10 ${
                                suggestion.active
                                  ? "bg-[#651FA8]/10"
                                  : "bg-white"
                              }`,
                            })}
                            key={suggestion.placeId}
                          >
                            <div className="flex items-center gap-3">
                              <div className="text-[#651FA8] text-xl">
                                {suggestion.types.includes("establishment")
                                  ? "🏢"
                                  : "📍"}
                              </div>
                              <div>
                                <div className="font-medium text-[#651FA8]">
                                  {suggestion.description}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </PlacesAutocomplete>
              </div>
            </div>
          )}
          {/* Scrollable Content */}
          <div className="overflow-y-auto px-4 sm:px-6 flex-1 min-h-0">
            <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-6">
              <div className="w-full flex md:flex-row flex-col items-center justify-center gap-2 sm:gap-4">
                {/* Venue Name */}
                <div
                  onClick={() => setShowSearch(true)}
                  className="cursor-pointer flex-1 w-full"
                >
                  <label className="block text-sm font-semibold text-[#651FA8] mb-2">
                    Venue Name
                  </label>
                  <div className="sm:p-3 p-2 rounded-lg border-2 border-[#651FA8]/30 bg-white hover:border-[#651FA8] transition-colors max-w-full overflow-hidden">
                    <input
                      value={name || "Search venue"}
                      className="overflow-x-auto w-full whitespace-nowrap"
                    />
                  </div>
                </div>

                {/* Venue Address */}
                <div
                  onClick={() => setShowSearch(true)}
                  className="cursor-pointer flex-1 w-full"
                >
                  <label className="block text-sm font-semibold text-[#651FA8] mb-2">
                    Venue Address
                  </label>
                  <div className="sm:p-3 p-2 rounded-lg border-2 border-[#651FA8]/30 bg-white hover:border-[#651FA8] transition-colors max-w-full overflow-hidden">
                    <input
                      value={address || "Search address"}
                      className="overflow-x-auto w-full whitespace-nowrap"
                    />
                  </div>
                </div>
              </div>

              {/* Themes Selection */}
              <div>
                <label className="block text-sm font-semibold text-[#651FA8] mb-2 sm:mb-3">
                  Event Themes (max 3)
                </label>
                <div className="flex flex-wrap md:gap-2 gap-1">
                  {Array.isArray(themes) &&
                    themes.map((theme) => (
                      <button
                        key={theme}
                        type="button"
                        onClick={() => toggleTheme(theme)}
                        className={`py-1.5 sm:text-sm text-xs rounded-full border-2 md:w-28 w-24 transition-all ${
                          selectedThemes.includes(theme)
                            ? "bg-[#651FA8] border-[#651FA8] text-white shadow-lg"
                            : "border-[#8A325D] text-[#8A325D] hover:bg-[#8A325D]/10"
                        }`}
                      >
                        {theme}
                      </button>
                    ))}
                </div>
              </div>

              {/* Date & Time */}
              <div className="flex gap-2 sm:gap-4">
                <div className="flex-1 w-full">
                  <label className="block text-sm font-semibold text-[#651FA8] mb-2">
                    Event Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full sm:p-3  p-2 rounded-lg border-2 border-[#651FA8]/30 bg-white focus:ring-2 focus:ring-[#651FA8] outline-none"
                    required
                  />
                </div>
                <div className="flex-1 w-full">
                  <label className="block text-sm font-semibold text-[#651FA8] mb-2">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full sm:p-3  p-2 rounded-lg border-2 border-[#651FA8]/30 bg-white focus:ring-2 focus:ring-[#651FA8] outline-none"
                    required
                  />
                </div>
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-semibold text-[#651FA8] mb-2">
                  Duration (24 hours only)
                </label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  step="0.5"
                  value={duration}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (isNaN(value)) return setDuration("");
                    if (value >= 1 && value <= 24) {
                      setDuration(value);
                    } else if (value > 24) {
                      toast.warn("Maximum duration allowed is 24 hours");
                      setDuration(24);
                    } else {
                      toast.warn("Minimum duration allowed is 1 hour");
                      setDuration(1);
                    }
                  }}
                  placeholder="4"
                  className="w-full sm:p-3  p-2 rounded-lg border-2 border-[#651FA8]/30 focus:ring-2 focus:ring-[#651FA8] outline-none"
                  required
                />
              </div>
            </form>
          </div>

          {/* Fixed Footer with Buttons */}
          <div className="p-3 sm:p-6 border-t border-gray-200 bg-white text-sm flex-shrink-0">
            <div className="flex justify-end space-x-4">
              <button
                onClick={handleSubmit}
                className="w-full py-3 sm:py-4 bg-gradient-to-r from-[#651FA8] to-[#8A325D] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-xl text-base sm:text-lg"
              >
                Create Event →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
